import { waitForPromise } from '@ember/test-waiters';
import Authenticator from 'ember-simple-auth/authenticators/base';
import type { LoginVerificationRequest } from 'frontend-payments/services/auth';
import { getApiBaseUrl } from 'frontend-payments/utils/api-config';
import { throwApiError } from 'frontend-payments/utils/api-helpers';

interface AuthenticateResponse {
  authenticated?: boolean;
}

export default class BackendAuthenticator extends Authenticator {
  async authenticate(
    email: string,
    code: string,
  ): Promise<AuthenticateResponse> {
    const response = await waitForPromise(
      fetch(`${getApiBaseUrl()}/login/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies in request and receive Set-Cookie headers
        redirect: 'manual', // Don't follow redirects automatically
        body: JSON.stringify({
          email,
          totp_code: code,
        } as LoginVerificationRequest),
      }),
    );

    // Handle 302 redirect for staff users
    // opaqueredirect: When using redirect: 'manual' with CORS, browsers return an opaque redirect response
    // (status 0, type 'opaqueredirect') instead of exposing the actual redirect details for security reasons
    if (response.status === 302 || response.type === 'opaqueredirect') {
      // Before reloading, store the authenticated state in localStorage
      // This ensures that after reload, the session can be restored
      const authenticatedData = {
        authenticator: 'authenticator:backend',
        authenticated: true,
      };

      // Manually persist to localStorage before reload
      // Using the same key that ember-simple-auth uses
      try {
        localStorage.setItem(
          'ember_simple_auth-session',
          JSON.stringify({ authenticated: authenticatedData }),
        );
      } catch (e) {
        console.warn('Failed to store session data before reload:', e);
      }

      window.location.reload();
      return new Promise(() => {});
    }

    if (!response.ok) {
      await throwApiError(response);
    }

    // For session-based auth, successful 200 response means authentication succeeded
    // The session cookie is automatically set by the browser
    // We return minimal data since the real auth state is in the session cookie
    return {
      authenticated: true,
    };
  }

  // Ember simple auth calls invalidate when logging out
  async invalidate(): Promise<void> {
    const response = await waitForPromise(
      fetch(`${getApiBaseUrl()}/logout`, {
        method: 'POST',
        credentials: 'include', // Include cookies to invalidate the session
        redirect: 'manual', // Don't follow redirects automatically
      }),
    );

    // Handle 302 redirect to login page
    // When using redirect: 'manual' with CORS, we get an opaque redirect response (status 0, type 'opaqueredirect')
    if (response.status === 302 || response.type === 'opaqueredirect') {
      // Backend wants us to redirect to login page
      window.location.href = '/login';
      // Return a promise that never resolves to prevent further processing
      return new Promise(() => {});
    }

    // If logout was successful (200), we can just return
    if (response.ok) {
      return;
    }

    // Otherwise, throw an error
    await throwApiError(response);
  }

  // Ember simple auth calls restore on app load with data from local storage
  // The data parameter contains what was previously stored in local storage
  restore(data: AuthenticateResponse = {}): Promise<AuthenticateResponse> {
    // For session-based authentication, we trust the localStorage data
    // The session cookie is the source of truth on the backend
    // If the session expires, the backend will return 401 and trigger re-authentication
    if (data.authenticated === true) {
      return Promise.resolve({ authenticated: true });
    }

    return Promise.reject(new Error('No valid session data found'));
  }
}

declare module 'ember-simple-auth/authenticators/base' {
  interface Registry {
    'authenticator:backend': BackendAuthenticator;
  }
}
